import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EmailTemplateOptions } from '../../../../../../../backend/src/types/types';
import { TablePagination } from '../../../../components/table/table-pagination';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSendEmail } from '@/api/mutations/useSendEmail';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';

const emailFormSchema = z.object({
  email: z.email({ message: 'Please enter a valid email address' }),
});

type EmailForm = z.infer<typeof emailFormSchema>;

interface UserTableProps {
  users: User[];
  addedUsers: User[];
  onUserChange: (users: User[]) => void;
  matterCreatorId?: string;
}

export function UserTable({ users, addedUsers, onUserChange, matterCreatorId }: UserTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const { user } = useUserContext();
  const { mutate: sendEmail } = useSendEmail();

  const form = useForm<EmailForm>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleInviteUser = form.handleSubmit((data) => {
    trackCustomEvent(TrackingEventsV2.InvitedUserWithNoAccount, {
      userId: user?.id,
      orgId: user?.organisationId,
    });
    sendEmail({
      templateType: EmailTemplateOptions.Invite,
      to: data.email,
      from: user?.email ?? '',
    });
    form.reset();
    setIsInviteDialogOpen(false);
  });

  const handleInviteFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    handleInviteUser(e);
  };

  // Separate users into added and available
  const { availableUsers } = useMemo(() => {
    const added: User[] = [];
    const available: User[] = [];

    users.forEach((u) => {
      if (addedUsers.some((au) => au.id === u.id) || u.id === matterCreatorId) {
        added.push(u);
      } else {
        available.push(u);
      }
    });

    return { availableUsers: available };
  }, [users, addedUsers, matterCreatorId]);

  const handleAddUser = (user: User) => {
    trackCustomEvent(TrackingEventsV2.AddTeamMemberOnMatterCreator, { userId: user.id });
    onUserChange([...addedUsers, user]);
  };

  const handleRemoveUser = (userId: string) => {
    if (userId === matterCreatorId) return; // Can't remove matter creator
    trackCustomEvent(TrackingEventsV2.RemoveTeamMemberOnMatterCreator, { userId });
    onUserChange(addedUsers.filter((u) => u.id !== userId));
  };

  const availableTable = useReactTable({
    data: availableUsers,
    columns: [
      {
        accessorKey: 'email',
        header: ({ column }) => {
          return (
            <div className="text-left">
              <div
                className="flex items-center gap-1 hover:cursor-pointer"
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              >
                Email
                <Icon className="lucide--arrow-up-down" />
              </div>
            </div>
          );
        },
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div
              className={`text-left lowercase ${isDisabled ? 'text-gray-400' : ''}`}
              data-testid={`available-user-email-${row.original.id}`}
            >
              {row.getValue('email')}
              {isDisabled && <span className="ml-2 text-xs text-gray-500">(disabled)</span>}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleAddUser(row.original)}
                disabled={isDisabled}
                data-testid={`add-user-${row.original.id}`}
              >
                <Icon className="lucide--plus" />
                Add
              </Button>
            </div>
          );
        },
      },
    ],
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      globalFilter,
    },
  });

  const hasAvailableResults = availableTable.getRowModel().rows.length > 0;
  const selectedCount = useMemo(() => addedUsers.length, [addedUsers]);

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between gap-8">
        <div className="flex items-center gap-4">
          <SearchInput
            placeholder="Search available users..."
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-sm"
          />
          {selectedCount > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-200"
                  data-testid="added-users-badge"
                >
                  Users in Matter ({selectedCount})
                  <Icon className="lucide--chevron-down" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96" align="start">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Added Users</h3>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{selectedCount}</span>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {addedUsers.length > 0 ? (
                      <div className="space-y-1">
                        {addedUsers.map((addedUser) => (
                          <div
                            key={addedUser.id}
                            className="flex items-center justify-between border-b border-gray-100 bg-white p-2"
                          >
                            <div className="text-left lowercase" data-testid={`added-user-email-${addedUser.id}`}>
                              {addedUser.email}
                              {addedUser.id === user?.id && <span className="ml-2 text-xs text-gray-500">(You)</span>}
                              {addedUser.id === matterCreatorId && addedUser.id !== user?.id && (
                                <span className="ml-2 text-xs text-gray-500">(Creator)</span>
                              )}
                            </div>
                            {addedUser.id !== matterCreatorId && (
                              <Button
                                size="sm"
                                onClick={() => handleRemoveUser(addedUser.id)}
                                data-testid={`remove-user-${addedUser.id}`}
                              >
                                <Icon className="lucide--x" />
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-sm text-gray-500">No users added yet</div>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-button" variant="primary">
              Invite User
              <Icon className="lucide--user-plus" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite User to Wexler</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={handleInviteFormSubmit} className="space-y-4">
                <p className="text-sm text-gray-600">
                  Can't find the user you're looking for? Enter the email address of the person you'd like to invite to
                  join your team on Wexler.
                </p>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="name@example.com" type="email" autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-between gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsInviteDialogOpen(false);
                      form.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" disabled={!form.watch('email')?.trim()}>
                    Send Invitation
                    <Icon className="lucide--send" />
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Available Users Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Available Users</h3>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{availableUsers.length}</span>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white">
          <Table>
            <TableHeader>
              {availableTable.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {hasAvailableResults ? (
                availableTable.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className={row.original.disabled ? 'bg-gray-50 opacity-50' : ''}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} className="h-48">
                    <div className="flex flex-col items-center justify-center gap-4 py-8">
                      <div className="rounded-full bg-gray-100 p-4">
                        <Icon className="lucide--user-round-search h-8 w-8 text-gray-400" />
                      </div>
                      <div className="space-y-2 text-center">
                        <p className="text-sm font-medium text-gray-900">
                          {globalFilter ? `No users found matching "${globalFilter}"` : 'No available users'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {globalFilter
                            ? 'Try adjusting your search or invite them to join Wexler'
                            : 'All users have been added'}
                        </p>
                      </div>
                      {globalFilter && (
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            setIsInviteDialogOpen(true);
                          }}
                          variant="primary"
                          data-testid="invite-from-search-button"
                        >
                          Invite User
                          <Icon className="lucide--user-plus" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {hasAvailableResults && <TablePagination table={availableTable} plural="users" />}
      </div>
    </div>
  );
}
